import React from 'react'
import { Loader2, PackageOpen } from 'lucide-react'

interface SectionGridProps {
  title: string
  subtitle: string
  loading?: boolean
  error?: string | null
  emptyText?: string
  loadingText?: string
  gridClassName?: string
  rightContent?: React.ReactNode
  children?: React.ReactNode
}

export const SectionGrid: React.FC<SectionGridProps> = ({
  title,
  subtitle,
  loading = false,
  error = null,
  emptyText = 'No items available',
  loadingText = 'Loading...',
  gridClassName = 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8',
  rightContent,
  children
}) => {
  const isEmpty = !loading && !error && React.Children.count(children) === 0

  return (
    <div className="pt-6">
      {/* Header — hidden when empty */}
      {!isEmpty && (
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          </div>
          {rightContent && <div>{rightContent}</div>}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-black animate-spin" />
            <p className="text-sm text-gray-600">{loadingText}</p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-red-600 mb-2">Something went wrong</p>
            <p className="text-sm text-gray-600">{error}</p>
          </div>
        </div>
      )}

      {/* Empty */}
      {isEmpty && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-4 text-center max-w-xs">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: '#f5ede0', border: '1px solid #e0d4be' }}
            >
              <PackageOpen className="w-8 h-8" style={{ color: '#7a6020' }} />
            </div>
            <p className="text-base font-semibold text-gray-800">{emptyText}</p>
          </div>
        </div>
      )}

      {/* Grid */}
      {!loading && !error && !isEmpty && (
        <div className={gridClassName}>{children}</div>
      )}
    </div>
  )
}
